import { useFormik } from "formik";
import { observer } from 'mobx-react-lite';
import { useEffect } from "react";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useAddLabelStore, usePipedriveStore } from "..";
import useAddLabelValidationSchema from "../hooks/useAddLabelValidationSchema";
import { useConditionallyClosable } from "../hooks/useClosable";
import { ClosableOrder } from "../store/ClosableStore";
import commonStyles from './Common.module.css';
import StringInput from "./inputs/StringInput";
import Modal from "./Modal";
import Spinner from "./spinners/Spinner";
import { showErrorToast } from "./ToastContainer";

function AddLabel() {
    const addLabelStore = useAddLabelStore();
    const pipedriveStore = usePipedriveStore();
    const validationSchema = useAddLabelValidationSchema();

    const formik = useFormik<IAddLabelState>({
        initialValues: AddLabelState.empty(),
        validationSchema: toFormikValidationSchema(validationSchema),
        onSubmit: async (values) => {
            try {
                const label = values.label!.trim();
                const labelId = pipedriveStore.labelOptions.find(o => o.label.toLowerCase() === label.toLowerCase())?.id || await pipedriveStore.createLabelOption(label);
                addLabelStore.addLabel(labelId);
            } catch (e) {
                showErrorToast();
            }
        },
    });

    const close = () => {
        if (!formik.isSubmitting)
            addLabelStore.close();
    };

    useConditionallyClosable(ClosableOrder.High, close, addLabelStore.isOpen());

    useEffect(() => {
        if (addLabelStore.isOpen())
            formik.resetForm({ values: AddLabelState.empty() });
    }, [addLabelStore.isOpen()]);

    if (!addLabelStore.isOpen())
        return null;

    return (
        <Modal
            header="New Label"
            onClose={close}
        >
            <StringInput formik={formik} label="LABEL NAME" name="label" placeholder="Label name" />
            <div className={[commonStyles.modalControls, commonStyles.controlGroup].join(" ")}>
                <button className={commonStyles.secondaryButton} disabled={formik.isSubmitting} onClick={close}>Cancel</button>
                {formik.isSubmitting ? (
                    <Spinner />
                ) : (
                    <button className={commonStyles.primaryButton} onClick={() => formik.handleSubmit()}>Save</button>
                )}
            </div>
        </Modal>
    );
}

export default observer(AddLabel);

interface IAddLabelState {
    label: string | null;
}

class AddLabelState implements IAddLabelState {
    label: string | null = null;

    static empty() {
        return new AddLabelState();
    }
}
